/**
 * Credentials Export Tab Component
 * Shows license, credential, and NREMT data in MUI DataGrid with CSV export
 */

import { Box, Typography, Alert, Chip, Skeleton } from '@mui/material'
import {
  Badge as LicenseIcon,
  School as CredentialIcon,
  VerifiedUser as NREMTIcon,
} from '@mui/icons-material'
import { DataGridPremium, GridColDef } from '@mui/x-data-grid-premium'
import dayjs from 'dayjs'
import DataGridToolbar from 'src/v3/components/data-grid-toolbar'
import { useCredentialsData } from '../../hooks/use-credentials-data'
import type { CredentialsExportItem } from '../../types'

interface CredentialsExportTabProps {
  linkedServiceID: string
}

/**
 * Custom DataGrid toolbar for credentials export
 */
function CustomToolbar() {
  return <DataGridToolbar fileName="credentials-license-report" />
}

export default function CredentialsExportTab({
  linkedServiceID,
}: CredentialsExportTabProps) {
  // Fetch credentials data
  let { data, isLoading, error } = useCredentialsData(linkedServiceID, true)
  let credentialsData = data ?? []

  // DataGrid columns
  let columns: GridColDef<CredentialsExportItem>[] = [
    {
      field: 'userName',
      headerName: 'User Name',
      width: 200,
      flex: 1,
    },
    {
      field: 'email',
      headerName: 'Email',
      width: 250,
      flex: 1,
    },
    {
      field: 'type',
      headerName: 'Type',
      width: 120,
      renderCell: (params) => {
        let Icon =
          params.value === 'License'
            ? LicenseIcon
            : params.value === 'NREMT'
              ? NREMTIcon
              : CredentialIcon

        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Icon sx={{ fontSize: 16 }} />
            {params.value}
          </Box>
        )
      },
    },
    {
      field: 'name',
      headerName: 'Name',
      width: 150,
      flex: 1,
    },
    {
      field: 'number',
      headerName: 'Number',
      width: 150,
    },
    {
      field: 'state',
      headerName: 'State',
      width: 80,
      renderCell: (params) => params.value || 'N/A',
    },
    {
      field: 'expiration',
      headerName: 'Expiration',
      width: 120,
      renderCell: (params) => {
        if (!params.value) return 'N/A'
        return dayjs(params.value).format('MMM D, YYYY')
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 130,
      renderCell: (params) => {
        let color: 'success' | 'warning' | 'error' = (() => {
          switch (params.value) {
            case 'expired':
              return 'error'
            case 'expiring-soon':
              return 'warning'
            case 'active':
              return 'success'
            default:
              return 'success'
          }
        })()

        return (
          <Chip
            label={params.value.replace('-', ' ')}
            color={color}
            size="small"
            variant="outlined"
            sx={{ textTransform: 'capitalize' }}
          />
        )
      },
    },
    {
      field: 'daysToExpiration',
      headerName: 'Days to Expiration',
      width: 150,
      renderCell: (params) => {
        if (params.value === undefined || params.value === null) return 'N/A'

        if (params.value < 0) {
          return `${Math.abs(params.value)} days expired`
        }

        return `${params.value} days remaining`
      },
    },
    {
      field: 'dateAdded',
      headerName: 'Date Added',
      width: 120,
      renderCell: (params) => dayjs(params.value).format('MMM D, YYYY'),
    },
  ]

  // Get rows with unique IDs
  let rows = credentialsData.map((item, index) => ({
    ...item,
    id: `${item.type}-${item.name}-${item.number}-${index}`,
  }))

  // Get summary stats
  let totalItems = credentialsData.length
  let licenses = credentialsData.filter(
    (item) => item.type === 'License',
  ).length
  let credentials = credentialsData.filter(
    (item) => item.type === 'Credential',
  ).length
  let nremt = credentialsData.filter((item) => item.type === 'NREMT').length
  let expired = credentialsData.filter(
    (item) => item.status === 'expired',
  ).length
  let expiringSoon = credentialsData.filter(
    (item) => item.status === 'expiring-soon',
  ).length

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        Failed to load credentials data: {error.message}
      </Alert>
    )
  }

  if (isLoading) {
    return <Skeleton variant="rectangular" height={600} />
  }

  if (!credentialsData || credentialsData.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h6" color="text.secondary">
          No credentials or licenses found
        </Typography>
        <Typography variant="body2" color="text.secondary">
          There are no credentials or licenses for this linked service.
        </Typography>
      </Box>
    )
  }

  return (
    <>
      {/* Summary Stats */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <Chip label={`${totalItems} Total`} size="small" variant="outlined" />
        <Chip
          label={`${licenses} Licenses`}
          size="small"
          variant="outlined"
          color="primary"
        />
        <Chip
          label={`${credentials} Credentials`}
          size="small"
          variant="outlined"
          color="secondary"
        />
        <Chip
          label={`${nremt} NREMT`}
          size="small"
          variant="outlined"
          color="info"
        />
        {expired > 0 && (
          <Chip
            label={`${expired} Expired`}
            size="small"
            variant="outlined"
            color="error"
          />
        )}
        {expiringSoon > 0 && (
          <Chip
            label={`${expiringSoon} Expiring Soon`}
            size="small"
            variant="outlined"
            color="warning"
          />
        )}
      </Box>

      {/* DataGrid */}
      <Box sx={{ height: 600, width: '100%' }}>
        <DataGridPremium
          rows={rows}
          columns={columns}
          loading={isLoading}
          slots={{
            toolbar: CustomToolbar,
          }}
          showToolbar
          initialState={{
            pagination: {
              paginationModel: { pageSize: 25 },
            },
            sorting: {
              sortModel: [{ field: 'userName', sort: 'asc' }],
            },
          }}
          pageSizeOptions={[25, 50, 100]}
          checkboxSelection
          disableRowSelectionOnClick
          sx={{
            '& .MuiDataGrid-toolbarContainer': {
              borderBottom: '1px solid',
              borderColor: 'divider',
              p: 2,
            },
          }}
        />
      </Box>
    </>
  )
}
